import React, { useMemo, useState } from "react";
import PageLoader from "components/ui/PageLoader";
import "../assets/css/style.css";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import { FaPlus } from "react-icons/fa6";
import { Modal } from "@beleef/ui";
import Table from "components/ui/Table";
import Button from "components/ui/Button";
import {
  useCreateNetworkMutation,
  useDeleteNetworkMutation,
  useGetNetworkByIdQuery,
  useGetNetworksQuery,
  useUpdateNetworkMutation,
} from "apis/network";
import NetworkEdit from "components/network/NetworkEdit";
import NetworkView from "components/network/NetworkView";
import {
  STATUS_PILL,
  formatNetworkDate,
  getAllowedOverrides,
  getSharedRecordTypes,
} from "utils/network";

const PolicyPill = ({ label, className }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${className}`}
  >
    {label}
  </span>
);

// Both columns list only what is switched ON — the off state is the default,
// and listing it would bury the exceptions that matter.
//
// `enabled` is whatever that column switched on: shared record types in one,
// allowed group settings in the other. Both are { key, name } lists, so this
// cell stays generic rather than being written twice.
const PolicyCell = ({ enabled, className }) =>
  enabled.length === 0 ? (
    <span className="text-sm text-darkgray">None</span>
  ) : (
    <div className="flex flex-wrap gap-1">
      {enabled.map((entry) => (
        <PolicyPill key={entry.key} label={entry.name} className={className} />
      ))}
    </div>
  );

const Networks = () => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const pageSize = 10;

  const {
    data,
    isPending: isDataPending,
    isError: isDataError,
    error: dataError,
  } = useGetNetworksQuery();

  const { mutate: createNetwork, isLoading: isCreateLoading } =
    useCreateNetworkMutation();
  const { mutate: updateNetwork } = useUpdateNetworkMutation();
  const { mutate: deleteNetwork, isLoading: isDeleteLoading } =
    useDeleteNetworkMutation();

  // The list response has no organizations on it — only the by-id endpoint
  // returns them, so the detail view fetches when its modal opens.
  const { data: networkDetail } = useGetNetworkByIdQuery(
    isViewOpen ? selectedId : null,
  );

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "name",
        header: "Network",
        isFilter: true,
        minWidth: "180px",
      },
      {
        accessorKey: "slug",
        header: "Slug",
        cell: (row) => (
          <span className="font-mono text-sm">{row.slug || "-"}</span>
        ),
        isFilter: true,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (row) => {
          const status = row.status || "active";
          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                STATUS_PILL[status] || STATUS_PILL.active
              }`}
            >
              {status}
            </span>
          );
        },
        isFilter: false,
      },
      {
        accessorKey: "organizationCount",
        header: "Organizations",
        cell: (row) => <span>{row.organizationCount || 0}</span>,
        isFilter: false,
      },
      {
        accessorKey: "sharing",
        header: "Shared Across Orgs",
        cell: (row) => (
          <PolicyCell
            enabled={getSharedRecordTypes(row.sharing)}
            className="bg-green-100 text-green-800"
          />
        ),
        isFilter: false,
        minWidth: "170px",
      },
      {
        accessorKey: "controlledBy",
        header: "Overrides Allowed",
        cell: (row) => (
          <PolicyCell
            enabled={getAllowedOverrides(row)}
            className="bg-blue-100 text-blue-800"
          />
        ),
        isFilter: false,
        minWidth: "160px",
      },
      {
        accessorKey: "brandAssets",
        header: "Brand Assets",
        cell: (row) => <span>{row.brandAssets?.length || 0}</span>,
        isFilter: false,
      },
      {
        accessorKey: "updatedAt",
        header: "Updated",
        cell: (row) => (
          <span className="text-sm">{formatNetworkDate(row.updatedAt)}</span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => (
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsEditOpen(true);
              }}
              className="edit-icon"
              title="Edit Network"
            >
              <FiEdit2 fontSize={20} />
            </button>
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsDeleteOpen(true);
              }}
              className="delete-icon"
              title="Delete Network"
            >
              <FiTrash2 fontSize={20} />
            </button>
          </div>
        ),
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "100px",
      },
    ],
    [],
  );

  if (isDataPending) {
    return <PageLoader />;
  }

  if (isDataError) {
    return (
      <div className="container mx-auto">
        <div className="text-center py-8">
          <p className="text-red-600">
            Error loading networks: {dataError?.message}
          </p>
        </div>
      </div>
    );
  }

  const selectedNetwork = data?.find((network) => network._id === selectedId);
  const attachedOrganizations = selectedNetwork?.organizationCount || 0;

  const handleEditSubmit = (formData) => {
    updateNetwork({ id: selectedId, ...formData });
    setIsEditOpen(false);
  };

  const handleCreateSubmit = (formData) => {
    createNetwork(formData);
    setIsAddOpen(false);
  };

  const handleDelete = () => {
    deleteNetwork(selectedId);
    setIsDeleteOpen(false);
  };

  const handleViewRowClick = (row) => {
    setSelectedId(row._id);
    setIsViewOpen(true);
  };

  const AddNetworkButton = () => (
    <Button
      className="black-button flex items-center"
      onClick={() => setIsAddOpen(true)}
      disabled={isCreateLoading}
    >
      <FaPlus size={14} className="mr-2" />
      Add Network
    </Button>
  );

  return (
    <>
      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Edit Network"
        className="max-w-5xl"
      >
        <NetworkEdit
          key={selectedId || "network-edit"}
          initialData={selectedNetwork}
          onSubmit={handleEditSubmit}
        />
      </Modal>

      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Add Network"
        className="max-w-5xl"
      >
        <NetworkEdit
          key={isAddOpen ? "network-add-open" : "network-add-closed"}
          initialData={{}}
          onSubmit={handleCreateSubmit}
        />
      </Modal>

      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title="Delete Network"
        footer={
          <div className="flex gap-2 justify-end">
            <Button
              onClick={() => setIsDeleteOpen(false)}
              className="gray-button"
              disabled={isDeleteLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              className="black-button"
              loading={isDeleteLoading}
              disabled={attachedOrganizations > 0}
            >
              Delete Network
            </Button>
          </div>
        }
      >
        {attachedOrganizations > 0 ? (
          <p>
            "{selectedNetwork?.name}" still has {attachedOrganizations}{" "}
            organization
            {attachedOrganizations === 1 ? "" : "s"} attached. Move them to
            another network before deleting it.
          </p>
        ) : (
          <p>
            Are you sure you want to delete "{selectedNetwork?.name}"? This
            action cannot be undone.
          </p>
        )}
      </Modal>

      <Modal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title="Network Details"
        className="max-w-5xl"
      >
        <NetworkView network={networkDetail || selectedNetwork} />
      </Modal>

      <div className="container mx-auto">
        <Table
          title="Networks"
          columns={columns}
          data={data || []}
          pageSize={pageSize}
          ButtonCmp={AddNetworkButton}
          emptyText="No networks found"
          onRowClick={handleViewRowClick}
        />
      </div>
    </>
  );
};

export default Networks;
